import { useRef, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { ROLES } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const IMPORT_ROLES = ["direzione", "responsabile", "assistente"];
const multi = (e) => Array.from(e.target.selectedOptions).map((o) => o.value);

const Row = ({ r, i, set, companies, deps }) => {
  const off = r.status !== "new";
  return (
    <TableRow className={off ? "bg-muted/40 text-muted-foreground" : ""} data-testid={`import-row-${i}`}>
      <TableCell><Checkbox checked={r.include} disabled={off} onCheckedChange={(v) => set(i, { include: !!v })} data-testid={`import-include-${i}`} /></TableCell>
      <TableCell><Input disabled={off} className="h-8 w-44" value={r.name} onChange={(e) => set(i, { name: e.target.value })} data-testid={`import-name-${i}`} /><div className="mt-1 font-mono text-[11px]">{r.email}</div><div className="text-[11px]">{r.title}</div></TableCell>
      <TableCell><select disabled={off} className="h-8 border bg-white px-1 text-sm" value={r.role} onChange={(e) => set(i, { role: e.target.value, ...(e.target.value === "direzione" ? { monthly_report: true } : {}) })} data-testid={`import-role-${i}`}>{IMPORT_ROLES.map((k) => <option key={k} value={k}>{ROLES[k]}</option>)}</select></TableCell>
      <TableCell>
        <label className="mb-1 flex items-center gap-1 text-xs"><Checkbox disabled={off} checked={r.group_scope} onCheckedChange={(v) => set(i, { group_scope: !!v })} data-testid={`import-group-${i}`} />Tutto il gruppo</label>
        {!r.group_scope && <select multiple disabled={off} className="h-20 w-48 border bg-white text-xs" value={r.company_ids} onChange={(e) => set(i, { company_ids: multi(e) })} data-testid={`import-companies-${i}`}>{companies.map((c) => <option key={c.id} value={c.id}>{c.legal_name}</option>)}</select>}
      </TableCell>
      <TableCell>{r.role === "direzione" ? <span className="text-xs">Tutte le aree</span> :
        <select multiple disabled={off} className="h-20 w-44 border bg-white text-xs" value={r.departments} onChange={(e) => set(i, { departments: multi(e) })} data-testid={`import-departments-${i}`}>{deps.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}</select>}</TableCell>
      <TableCell><Checkbox disabled={off || r.role === "assistente"} checked={r.monthly_report && r.role !== "assistente"} onCheckedChange={(v) => set(i, { monthly_report: !!v })} data-testid={`import-report-${i}`} /></TableCell>
      <TableCell className="max-w-[220px] text-xs">{r.warnings.map((w, j) => <div key={j} className={r.status === "new" ? "text-amber-700" : "text-red-700"}>{w}</div>)}</TableCell>
    </TableRow>
  );
};

export const UserImport = ({ companies, deps, onDone }) => {
  const ref = useRef(null);
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const upload = async (f) => {
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { const r = await api.post("/users/import/preview", fd); setRows(r.data.rows.map((x) => ({ ...x, include: x.status === "new" }))); } catch (e) { toast.error(errMsg(e)); }
    ref.current.value = "";
  };
  const set = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const chosen = (rows || []).filter((r) => r.include && r.status === "new");
  const commit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/users/import/commit", chosen.map(({ email, name, role, group_scope, company_ids, departments, monthly_report, title }) => ({ email, name, role, group_scope, company_ids, departments: role === "direzione" ? [] : departments, monthly_report, title })));
      const n = r.data.created.length;
      if (n) toast.success(n === 1 ? "1 utente creato: l'invito parte tra pochi secondi" : `${n} utenti creati: gli inviti partono a intervalli nei prossimi minuti`);
      if (n) setTimeout(onDone, n * 4000 + 3000);
      r.data.errors.forEach((e) => toast.error(`${e.email}: ${e.error}`));
      if (!r.data.errors.length) setRows(null);
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <>
      <input ref={ref} type="file" accept=".xlsx" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="users-import-file" />
      <Button variant="outline" onClick={() => ref.current.click()} data-testid="users-import-button"><FileSpreadsheet className="mr-1 h-4 w-4" />Importa da Excel</Button>
      <Dialog open={!!rows} onOpenChange={(o) => !o && setRows(null)}>
        <DialogContent className="max-h-[90vh] max-w-6xl overflow-y-auto" data-testid="users-import-dialog">
          <DialogHeader>
            <DialogTitle>Anteprima importazione utenti</DialogTitle>
            <DialogDescription>Controlla ruolo, società e reparto proposti per ogni persona. Alla conferma creo gli account e invio a ciascuno l'email di benvenuto per scegliere la password.</DialogDescription>
          </DialogHeader>
          <div className="overflow-x-auto border">
            <Table>
              <TableHeader><TableRow className="bg-muted/50"><TableHead>Crea</TableHead><TableHead>Persona</TableHead><TableHead>Ruolo</TableHead><TableHead>Società visibili</TableHead><TableHead>Reparti</TableHead><TableHead>Report mensile</TableHead><TableHead>Note</TableHead></TableRow></TableHeader>
              <TableBody>{(rows || []).map((r, i) => <Row key={r.email || i} r={r} i={i} set={set} companies={companies} deps={deps} />)}</TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground">Ctrl/Cmd + clic per scegliere più società o reparti.</span>
            <Button disabled={busy || !chosen.length} onClick={commit} data-testid="users-import-confirm">{busy ? "Creazione…" : chosen.length === 1 ? "Crea 1 utente e invia l'invito" : `Crea ${chosen.length} utenti e invia gli inviti`}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
